import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { readRawUserData } from '../../services/userData';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const raw = readRawUserData();
  // Use nullish coalescing so an empty string (if ever intentionally stored) is not overridden
  const storedDocId = raw?.docId ?? raw?.data?.docId ?? raw?.body?.data?.docId;
  const hasUserData = !!storedDocId;
  const lastKnownDocIdRef = useRef<string | undefined>(typeof storedDocId === 'string' ? storedDocId : undefined);
  if (typeof storedDocId === 'string' && storedDocId) lastKnownDocIdRef.current = storedDocId;
  const docId = storedDocId ?? lastKnownDocIdRef.current;

  const [redirectTarget, setRedirectTarget] = useState<string | null>(null);

  // One-time remediation effect if auth present but user-data missing
  useEffect(() => {
    if (isAuthenticated && !hasUserData && !redirectTarget) {
      try { localStorage.clear(); } catch { /* ignore */ }
      try { logout(); } catch { /* ignore */ }
      const docIdFromUrl = searchParams.get('docId');
      const target = docIdFromUrl ? `/?docId=${encodeURIComponent(docIdFromUrl)}` : '/';
      setRedirectTarget(target);
    }
  }, [isAuthenticated, hasUserData, redirectTarget, logout, searchParams]);

  // Primary unauthenticated gate (only if we are NOT in the special remediation redirect)
  if (!isAuthenticated && !redirectTarget) {
    const iv = raw?.iv ?? raw?.IV;
    const query = typeof iv === 'string' && iv ? `?IV=${encodeURIComponent(iv)}` : '';
    const verificationPath = docId
      ? `/${encodeURIComponent(docId)}${query}`
      : '/';
    return <Navigate to={verificationPath} replace state={{ from: location }} />;
  }

  if (redirectTarget) {
    return <Navigate to={redirectTarget} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
